import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  ArrowRight,
  Landmark,
  Shield,
  Vote,
  HeartHandshake,
  GraduationCap,
  Users,
  HandHeart,
  Leaf,
  Globe,
  Quote,
  ScrollText,
} from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import { EmptyState, Badge } from "@/components/common/Primitives";
import { membersService } from "@/services/membersService";
import { useUnits, useMemberCount, useAbout } from "@/hooks/useSiteQueries";
import { seedAboutContent } from "@/data/seed/siteContent";
import {
  membershipBenefits,
  membershipBenefitsIntro,
  membershipBenefitsMotto,
  membershipBenefitsSummary,
} from "@/data/seed/membershipBenefits";
import { membershipAims, membershipAimsIntro, membershipAimsTitle } from "@/data/seed/membershipAims";
import { withMemberCountInText } from "@/lib/memberCountDisplay";

const BENEFIT_ICONS = [Landmark, Shield, Vote, HeartHandshake, GraduationCap, Users, HandHeart, Leaf, Globe];

/** Set to true to restore the public membership lookup form. */
const SHOW_MEMBERSHIP_SEARCH = false;

export default function MembershipSearchPage() {
  const { data: units = [] } = useUnits();
  const { data: memberCount } = useMemberCount();
  const { data: about } = useAbout();
  const cta = about?.cta ?? seedAboutContent.cta;
  const [query, setQuery] = useState("");
  const [unitSlug, setUnitSlug] = useState("All");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  async function handleSearch(e) {
    e?.preventDefault();
    setSearching(true);
    setSearched(true);
    try {
      const { results: rows, tooShort } = await membersService.search({ query, unitSlug });
      setResults(tooShort ? "tooShort" : rows);
    } finally {
      setSearching(false);
    }
  }

  const unitName = (slug) => units.find((u) => u.slug === slug)?.parish_name || slug;

  return (
    <div data-testid="membership-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-24">
          <p className="eyebrow !text-gold-light mb-4">Membership</p>
          <h1 className="heading-xl !text-white max-w-3xl">
            {SHOW_MEMBERSHIP_SEARCH ? (
              <>
                Find your <span className="italic text-gold-light">membership record.</span>
              </>
            ) : (
              <>
                Be part of the <span className="italic text-gold-light">Bombay Catholic Sabha.</span>
              </>
            )}
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            {memberCount ? `${memberCount.toLocaleString("en-IN")}+ members and growing. ` : ""}
            {SHOW_MEMBERSHIP_SEARCH
              ? "Search by your membership number or name to confirm your record with the Sabha."
              : "Discover what membership means and how you can serve with love, service and fraternity."}
          </p>
        </div>
      </section>

      {SHOW_MEMBERSHIP_SEARCH && (
      <section className="container-bcs py-14 sm:py-16 max-w-4xl">
        <form onSubmit={handleSearch} className="card-bcs p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                className="input-bcs !pl-11 !py-3.5 !rounded-full"
                placeholder="Membership number (e.g. BCS-10001) or full name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="membership-search-input"
              />
            </div>
            <select
              className="input-bcs !py-3.5 !rounded-full sm:w-64"
              value={unitSlug}
              onChange={(e) => setUnitSlug(e.target.value)}
              data-testid="membership-unit-select"
            >
              <option value="All">All units</option>
              {units.map((u) => (
                <option key={u.slug} value={u.slug}>
                  {u.parish_name}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary !py-3.5" disabled={searching} data-testid="membership-search-btn">
              {searching ? "Searching…" : "Search"}
            </button>
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-ink-muted">
            <ShieldCheck size={15} className="text-green-700 shrink-0 mt-0.5" />
            For privacy, only non-sensitive details are shown: name, membership number, unit and status.
            Contact the BCS office to update your record.
          </p>
        </form>

        <div className="mt-8">
          {query.trim().length > 0 && query.trim().length < 3 && (
            <p className="text-sm text-ink-muted text-center" data-testid="membership-too-short-hint">
              Keep typing — enter at least 3 characters of your membership number or name.
            </p>
          )}
          {results === "tooShort" && (
            <EmptyState title="Keep typing…" message="Please enter at least 3 characters of your membership number or name." />
          )}
          {Array.isArray(results) && results.length === 0 && searched && (
            <EmptyState
              title="No matching record found"
              message="Check the spelling or membership number, or contact the BCS office for assistance."
            />
          )}
          {Array.isArray(results) && results.length > 0 && (
            <div className="card-bcs overflow-hidden" data-testid="membership-results">
              <div className="px-6 py-3.5 bg-cream-alt/70 border-b border-gray-100 text-xs uppercase tracking-wider font-semibold text-ink-muted">
                {results.length} record{results.length === 1 ? "" : "s"} found
              </div>
              <ul className="divide-y divide-gray-50">
                {results.map((m) => (
                  <li key={m.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6" data-testid={`member-result-${m.membership_no}`}>
                    <div className="flex-1">
                      <p className="font-medium text-ink">{m.full_name}</p>
                      <p className="text-xs text-ink-muted mt-0.5">
                        {unitName(m.unit_slug)} · Member since {m.joined_year}
                      </p>
                    </div>
                    <span className="font-mono text-sm text-maroon font-semibold">{m.membership_no}</span>
                    <div className="flex gap-2">
                      <Badge tone="gray">{m.membership_type}</Badge>
                      <Badge tone={m.status === "Active" ? "green" : "gray"}>{m.status}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      )}

      <section className="container-bcs py-20 sm:py-24" data-testid="membership-aims-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <span className="h-12 w-12 rounded-xl bg-maroon-50 flex items-center justify-center text-maroon">
              <ScrollText size={22} />
            </span>
            <p className="eyebrow mt-6">Our mandate</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-ink leading-tight">
              {membershipAimsTitle}
            </h2>
            <div className="mt-5 h-0.5 w-14 bg-gold" />
            <p className="mt-6 text-sm sm:text-base text-ink-soft leading-relaxed">{membershipAimsIntro}</p>
          </div>

          <ol className="lg:col-span-8 space-y-4 list-none m-0 p-0">
            {membershipAims.map((aim, i) => (
              <li
                key={i}
                className="group relative flex gap-5 sm:gap-6 rounded-xl border border-gray-100 bg-white shadow-card p-5 sm:p-6 hover:border-maroon/15 hover:shadow-card-hover transition-all"
                data-testid={`membership-aim-${i + 1}`}
              >
                <span
                  className="font-serif text-2xl sm:text-3xl font-semibold text-gold-dark/70 leading-none shrink-0 w-8 sm:w-10 tabular-nums"
                  aria-hidden
                >
                  {i + 1}
                </span>
                <p className="text-sm sm:text-[15px] text-ink-soft leading-relaxed pt-0.5 group-hover:text-ink transition-colors">
                  {aim}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-cream-alt border-y border-cream-dark" data-testid="membership-benefits-section">
        <div className="container-bcs py-20 sm:py-24">
          <SectionHeading
            eyebrow="Why join"
            title="Benefits of Being a Member of The Bombay Catholic Sabha"
            subtitle={membershipBenefitsIntro}
          />

          <p className="mt-6 text-sm text-ink-muted max-w-3xl">
            Some key benefits include:
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {membershipBenefits.map((benefit, i) => {
              const Icon = BENEFIT_ICONS[i] || Landmark;
              return (
                <article
                  key={benefit.title}
                  className="card-bcs p-6 sm:p-7 flex flex-col h-full group hover:border-maroon/20 transition-colors"
                  data-testid={`membership-benefit-${i + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <span className="font-serif text-2xl font-semibold text-gold-dark/80 leading-none shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-11 w-11 rounded-xl bg-maroon-50 flex items-center justify-center text-maroon shrink-0 group-hover:bg-maroon group-hover:text-white transition-colors">
                      <Icon size={20} />
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-lg font-semibold text-ink leading-snug">{benefit.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">
                    {withMemberCountInText(benefit.body, memberCount)}
                  </p>
                  {benefit.bullets && (
                    <ul className="mt-4 space-y-2">
                      {benefit.bullets.map((item) => (
                        <li key={item} className="text-sm text-ink-soft flex items-start gap-2.5">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-bcs p-8 sm:p-10 border-maroon/10 bg-maroon-50/30">
              <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon">
                <Quote size={20} />
              </span>
              <p className="mt-5 eyebrow">In one sentence</p>
              <blockquote className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-ink leading-snug">
                &ldquo;{membershipBenefitsSummary}&rdquo;
              </blockquote>
            </div>

            <div className="card-bcs p-8 sm:p-10 flex flex-col justify-center bg-maroon text-white border-maroon">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold-light">The Sabha&apos;s motto</p>
              <p className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-white leading-snug italic">
                &ldquo;{membershipBenefitsMotto}&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-maroon" data-testid="membership-cta-section">
        <div className="container-bcs py-20 text-center">
          <h2 className="heading-lg !text-white max-w-2xl mx-auto">{cta.title}</h2>
          <p className="mt-4 text-gold-light font-serif text-xl">{cta.subtitle}</p>
          <p className="mt-3 text-white/80 max-w-xl mx-auto leading-relaxed">{cta.motto}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-secondary" data-testid="membership-cta-contact-btn">
              Get in touch <ArrowRight size={16} />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-maroon rounded-full px-6 py-3 text-sm font-medium transition-colors"
              data-testid="membership-cta-about-btn"
            >
              About the Sabha
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
